import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getExpenses,
  getExpenseById,
  createExpense,
  updateExpense,
  deleteExpense,
  approveExpense,
  rejectExpense,
  getExpensesStats,
  uploadReceipt,
  downloadReceipt,
} from '../services/expenses.service';

/**
 * Hook to fetch expenses list
 * @param {Object} params - Query parameters
 * @returns {Object} Query result
 */
export const useExpenses = (params = {}) => {
  return useQuery({
    queryKey: ['expenses', params],
    queryFn: () => getExpenses(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch single expense
 * @param {number} id - Expense ID
 * @returns {Object} Query result
 */
export const useExpense = (id) => {
  return useQuery({
    queryKey: ['expenses', id],
    queryFn: () => getExpenseById(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch expenses statistics
 * @returns {Object} Query result
 */
export const useExpensesStats = () => {
  return useQuery({
    queryKey: ['expenses', 'stats'],
    queryFn: getExpensesStats,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to create expense
 * @returns {Object} Mutation result
 */
export const useCreateExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExpense,
    onSuccess: () => {
      // Invalidate expenses list and stats
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

/**
 * Hook to update expense
 * @returns {Object} Mutation result
 */
export const useUpdateExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateExpense(id, data),
    onSuccess: (data, variables) => {
      // Invalidate expenses list, specific expense, and stats
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

/**
 * Hook to delete expense
 * @returns {Object} Mutation result
 */
export const useDeleteExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteExpense,
    onSuccess: () => {
      // Invalidate expenses list and stats
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

/**
 * Hook to approve expense
 * @returns {Object} Mutation result
 */
export const useApproveExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }) => approveExpense(id, notes),
    onSuccess: () => {
      // Invalidate expenses list and stats
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

/**
 * Hook to reject expense
 * @returns {Object} Mutation result
 */
export const useRejectExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }) => rejectExpense(id, notes),
    onSuccess: () => {
      // Invalidate expenses list and stats
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

/**
 * Hook to upload receipt
 * @returns {Object} Mutation result
 */
export const useUploadReceipt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }) => uploadReceipt(id, file),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

/**
 * Hook to download receipt
 * @returns {Object} Mutation result
 */
export const useDownloadReceipt = () => {
  return useMutation({
    mutationFn: ({ id, originalName }) => downloadReceipt(id, originalName),
  });
};

